import { isEqual } from 'lodash-es';
import type { CustomerAddress, CartAddressInterface } from '~/modules/GraphQL/types';

const getBuildingInfo = (street: string) => {
  if (!street) {
    return { building: '', apartment: '' };
  }
  const streetArray = street.split(' / ');
  const apartment = streetArray[1] || '';
  const building = streetArray[0];
  return { building, apartment };
};

export const formatAddressReturnToData = (address: CartAddressInterface) => {
  const { apartment, building } = getBuildingInfo(address.street[1]);
  return {
    firstname: address.firstname,
    lastname: address.lastname,
    street: address.street[0],
    apartment,
    building,
    city: address.city,
    region: address.region.code,
    country_code: address.country.code,
    postcode: address.postcode,
    telephone: address.telephone,
    company: address.company,
    vat_id: address.vat_id,
  };
};

/**
 * Converts addresses that were:
 * * added to the logged in user's account
 * * saved in the cart (eg. completed the shipping address step and saved in the Checkout page, then went to some other page)
 * into the form data format used by Billing and Shipping forms in the Checkout page
 */
export const addressFromApiToForm = (address: CustomerAddress | CartAddressInterface) : CheckoutAddressForm => {
  const { apartment, building } = getBuildingInfo(address.street?.[1]);

  return {
    firstname: address.firstname,
    lastname: address.lastname,
    street: address.street?.[0],
    apartment,
    building,
    city: address.city,
    region: (address as CustomerAddress)?.region?.region_code ?? (address as CartAddressInterface).region.code,
    country_code: (address as CustomerAddress)?.country_code ?? (address as CartAddressInterface).country.code,
    postcode: address.postcode,
    telephone: address.telephone,
    company: address.company,
    vat_id: address.vat_id,
  };
};

/**
 * Converts locit addresses that were:
 * into the form data format used by Billing and Shipping forms in the Checkout page
 */
export const addressFromLocitApiToForm = (address: Record<string, any>) : CheckoutAddressForm => ({
  firstname: '',
  lastname: '',
  street: address.item.street,
  apartment: address.item.building,
  building: '',
  city: address.item.city,
  country_code: 'PL',
  postcode: address.item.zip,
  region: '',
  telephone: '',
  company: '',
  vat_id: '',
});

/**
 * Converts an address object from the VAT ID API format to the CheckoutAddressForm format.
 * @param address - The address object from the VAT ID API.
 * @returns The converted address object in the CheckoutAddressForm format.
 */
export interface PLVatIdData {
  address: {
    street?: string;
    city?: string;
    zip_code?: string;
  };
  name: string;
  nip: string;
  regon: string;
}

export interface VatIdData {
  address: string;
  name: string;
  vatNumber: string;
  countryCode: string;
}

export const addressFromPLVatIdApiToForm = (vatData: PLVatIdData) : CheckoutAddressForm => ({
  firstname: '',
  lastname: '',
  street: vatData.address?.street || '',
  apartment: '',
  building: '',
  city: vatData.address?.city || '',
  region: '',
  country_code: 'PL',
  postcode: vatData.address?.zip_code || '',
  telephone: '',
  company: vatData.name,
  vat_id: vatData.nip,
});

export const addressFromVatIdApiToForm = (vatData: VatIdData) : CheckoutAddressForm => ({
  firstname: '',
  lastname: '',
  street: vatData.address || '',
  apartment: '',
  building: '',
  city: '',
  region: '',
  country_code: vatData.countryCode,
  postcode: '',
  telephone: '',
  company: vatData.name,
  vat_id: vatData.vatNumber,
});

export interface CheckoutAddressForm {
  firstname: string;
  lastname: string;
  street: string;
  apartment: string;
  building: string;
  city: string;
  region: string;
  country_code: string;
  postcode: string;
  telephone: string;
  company: string,
  vat_id: string,
  customerNotes?: string;
}

export const getInitialCheckoutAddressForm = () : CheckoutAddressForm => ({
  firstname: '',
  lastname: '',
  street: '',
  apartment: '',
  building: '',
  city: '',
  region: '',
  country_code: 'PL',
  postcode: '',
  telephone: '',
  company: '',
  vat_id: '',
});

/**
 * Try to find an address from the user's saved addresses that exactly matches the address that is bound to a cart.
 *
 * `useShipping().save()``sends an addressId to Magento to set the shipping address on the cart,
 * but when you download the cart after that - the cart's endpoint response doesn't contain that addressId, just the address fields (street etc.)
 * So the only choice left is to try to compare the fields of the addresses.
 *
 * This function exists because if a user returns to a cart whose shipping address was set before, we want the user address to be highlighted in the SfAddressPicker component.
 *
 * @param customerAddresses The addresses saved in a user's account
 * @param cartAddress The address that is bound to the cart, @see Cart["billing_address"] Cart["shipping_addresses"]
 *
 */
export const findUserAddressIdenticalToSavedCartAddress = (
  customerAddresses: CustomerAddress[] | null,
  cartAddress: CartAddressInterface,
) : CustomerAddress => customerAddresses?.find(
  (userAddress) => isEqual(addressFromApiToForm(userAddress), addressFromApiToForm(cartAddress)),
) ?? null;

export const isAddressesEqual = (address1: CheckoutAddressForm, address2: CheckoutAddressForm) => isEqual(address1, address2);
